import { useEffect, useState} from 'react';
import React from 'react';
import * as Sentry from '@sentry/react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Landing }         from '@/pages/Landing';
import { SignIn }          from '@/pages/SignIn';
import { SignUp }          from '@/pages/SignUp';
import { AffiliateSignUp } from '@/pages/AffiliateSignUp';
import { AffiliatePortal } from '@/pages/AffiliatePortal';
import { ForgotPassword }  from '@/pages/ForgotPassword';
import { ResetPassword }   from '@/pages/ResetPassword';
import { DashboardRoute }  from '@/pages/DashboardRoute';
import { Onboarding }      from '@/pages/Onboarding';
const RedesignLanding = React.lazy(() => import('@/pages/RedesignLanding'));
const RedesignDashboard = React.lazy(() => import('@/pages/RedesignDashboard'));
import { TrialExpired, isTrialExpired } from '@/pages/TrialExpired';
import { PaymentBanner } from '@/components/PaymentBanner';

// Top-level error boundary. If anything in the React tree throws during
// render, we surface the actual error message + an escape hatch instead of
// silently unmounting to a blank page. This is what we needed to debug the
// post-onboarding "black screen" — without it React just rips the tree out.
class TopErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[TopErrorBoundary] render crash:', error, info.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg space-y-4 text-center">
          <div className="text-base font-bold text-fg">The app hit an error rendering.</div>
          <div className="text-2xs text-fg-subtle font-mono break-all bg-bg-elevated border border-border rounded-lg p-3 text-left">
            {this.state.error.message}
            {this.state.error.stack ? '\n\n' + this.state.error.stack.split('\n').slice(0, 6).join('\n') : ''}
          </div>
          <div className="flex gap-2 justify-center">
            <Button size="sm" variant="secondary" onClick={() => location.reload()}>Reload</Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                try {
                  document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict';
                  localStorage.clear();
                } catch { /* ignore */ }
                window.location.href = '/signin';
              }}
            >
              Sign out & start over
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

// Capture ?ref= from any landing URL and persist for the eventual signup.
// Lives in localStorage so the code survives navigation, refreshes, and
// even tab-closes until the user actually signs up (or explicitly clears).
const AFFILIATE_REF_KEY = 'telos:affiliate-ref';
function CaptureAffiliateRef() {
  const loc = useLocation();
  useEffect(() => {
    try {
      const params = new URLSearchParams(loc.search);
      const ref = params.get('ref');
      if (ref && ref.trim()) {
        localStorage.setItem(AFFILIATE_REF_KEY, ref.trim().toUpperCase());
      }
    } catch { /* ignore */ }
  }, [loc.search]);
  return null;
}
/**
 * Hostname-aware redirects. The app lives on app.telosintelligence.ai;
 * marketing (Landing, Pricing, FAQ, etc.) lives on telosintelligence.ai.
 *  - Visit /dashboard, /signin, /signup, /onboarding, /trial-expired,
 *    /forgot-password, /reset-password on www → redirect to app.
 *  - Visit / on app → redirect to www
 *
 * Auth cookies are set on .telosintelligence.ai so they survive the bounce.
 * In dev (localhost) nothing redirects.
 */
const APP_ONLY_PREFIXES = ['/signin', '/signup', '/affiliate-signup', '/onboarding', '/trial-expired', '/forgot-password', '/reset-password'];
function HostnameRouter() {
  const loc = useLocation();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname;
    if (host === 'localhost' || host.endsWith('.local') || host.startsWith('staging.')) return;
    const isAppHost = host === 'app.telosintelligence.ai';
    const isMarketingHost = host === 'telosintelligence.ai' || host === 'www.telosintelligence.ai';
    if (!isAppHost && !isMarketingHost) return;
    const path = loc.pathname || '/';
    // Legacy /dashboard URLs bounce to app subdomain root
    if (isMarketingHost && (path === '/dashboard' || path.startsWith('/dashboard/'))) {
      window.location.replace('https://app.telosintelligence.ai/' + loc.search + loc.hash);
      return;
    }
    const isAppPath = APP_ONLY_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
    if (isMarketingHost && isAppPath) {
      // marketing host trying to serve an app route — bounce to app subdomain
      window.location.replace('https://app.telosintelligence.ai' + path + loc.search + loc.hash);
      return;
    }
    // App host '/' is now the dashboard (was previously bounced to marketing).
    // No redirect — render the dashboard via HostAwareIndex.

  }, [loc.pathname, loc.search, loc.hash]);
  return null;
}

/** On app.telosintelligence.ai, / renders the dashboard. On marketing
 *  hosts (telosintelligence.ai / www.) / renders the Landing page. This
 *  keeps the dashboard at app./ (no /dashboard prefix) while preserving
 *  the marketing site at root of the apex. */
function HostAwareIndex() {
  if (typeof window !== 'undefined' && window.location.hostname === 'app.telosintelligence.ai') {
    return <RequireAuth><RoleAwareHome /></RequireAuth>;
  }
  return <Landing />;
}

// Splits the authenticated home by account type. Affiliate-only accounts
// (role='affiliate') are partners, not subscribers — they get the partner
// portal and never reach the paid dashboard or its panels. Everyone else
// goes through the trial/paywall gate as before.
function RoleAwareHome() {
  const { user } = useAuth();
  if (user?.role === 'affiliate') return <AffiliatePortal />;
  return <RequirePaid><><PaymentBanner /><DashboardRoute /></></RequirePaid>;
}

function DashboardLegacyRedirect() {
  const loc = useLocation();
  // Preserve the search string + hash so ?payment=success survives.
  return <Navigate to={{ pathname: '/', search: loc.search, hash: loc.hash }} replace />;
}

/**
 * App-root payment-sync hook. Fires the moment any route on the app subdomain
 * mounts with ?payment=success in the URL, BEFORE the deep route tree has had
 * a chance to remount or strip the query. Confirms the user's Whop membership
 * server-side, then sends them through /onboarding. Belt + braces — works
 * even if the route eventually lands on DashboardRoute too (idempotent: the
 * /api/payments/sync endpoint short-circuits if the plan is already set).
 */
function usePaymentSuccessSync() {
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('payment') !== 'success') return;
    // Strip the param so a refresh doesn't re-fire the sync.
    const cleanSearch = (() => {
      const u = new URL(window.location.href);
      u.searchParams.delete('payment');
      return u.searchParams.toString();
    })();
    console.log('[payment-sync] detected ?payment=success — firing /api/payments/sync');
    // Clean the URL eagerly (replaceState avoids triggering a re-mount loop).
    window.history.replaceState({}, '', window.location.pathname + (cleanSearch ? '?' + cleanSearch : '') + window.location.hash);
    api.post('/api/payments/sync', {})
      .then((res) => {
        const r = res as { updated?: boolean; reason?: string; plan?: string };
        if (r?.updated) {
          console.log('[payment-sync] subscription confirmed:', r.plan, '\u2192 /onboarding');
          navigate('/onboarding', { replace: true });
        } else {
          console.warn('[payment-sync] no matching subscription found:', r?.reason || 'unknown');
        }
      })
      .catch((err) => {
        console.error('[payment-sync] request failed:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
}

export function App() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <TopErrorBoundary>
    <CaptureAffiliateRef />
    <HostnameRouter />
    <PaymentSuccessWatcher />
    <Routes>
      <Route path="/" element={<HostAwareIndex />} />
      <Route path="/signin" element={<PublicOnly><SignIn /></PublicOnly>} />
      {/* /signup is NOT wrapped in PublicOnly — SignUp.tsx handles the
          already-signed-in case itself, prompting the user to sign out first.
          The old redirect-to-dashboard behaviour silently skipped onboarding
          when an existing session was present. */}
      <Route path="/signup" element={<SignUp />} />
      <Route path="/affiliate-signup" element={<AffiliateSignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
      {/* Legacy /dashboard URLs preserve the query string so post-payment
          redirects (?payment=success) survive the route rewrite. Stripping it
          here used to leave users hanging on Loading... */}
      <Route path="/dashboard/*" element={<DashboardLegacyRedirect />} />
      {/* Stripe-inspired redesign preview — staging only */}
      <Route path="/redesign" element={<React.Suspense fallback={null}><RedesignLanding /></React.Suspense>} />
      <Route path="/redesign/app" element={<React.Suspense fallback={null}><RedesignDashboard /></React.Suspense>} />
      <Route path="*" element={<DashboardLegacyRedirect />} />
    </Routes>
    </TopErrorBoundary>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/signin" state={{ from: loc.pathname }} replace />;
  return children;
}

/**
 * Trial-expiry paywall gate. Runs *after* RequireAuth has confirmed a logged-in
 * user. If the user's free trial has ended and they have not upgraded to a
 * paying plan, the dashboard is replaced with a full-screen upgrade blocker.
 */
function RequirePaid({ children }: { children: React.ReactElement }) {
  const { user, refreshUser } = useAuth();
  const [verified, setVerified] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  // Force-refresh user state on every mount of a paid route. The dashboard
  // never renders before the server has confirmed the latest plan state —
  // closes the stale-AuthContext window where a cached paid user blob could
  // briefly let a free user past the paywall.
  useEffect(() => {
    let cancelled = false;
    // Safety timeout: if refreshUser hasn't resolved in 5 seconds, give up
    // and fall back to the existing AuthContext.user — better to render with
    // possibly-stale state than leave the user stuck on Loading… forever.
    const safetyTimer = window.setTimeout(() => {
      if (!cancelled) {
        // eslint-disable-next-line no-console
        console.warn('[paywall] refreshUser took >5s, falling back to cached user state');
        setVerified(true);
      }
    }, 5000);
    refreshUser()
      .then((u) => {
        if (cancelled) return;
        window.clearTimeout(safetyTimer);
        if (!u) setVerifyFailed(true);
        setVerified(true);
        if (u && !isTrialExpired(u)) {
          // eslint-disable-next-line no-console
          console.log('[paywall] PASS user=' + u.id + ' plan=' + (u.plan || 'free') + ' whop=' + (u as unknown as { whopMembershipId?: string | null }).whopMembershipId);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        window.clearTimeout(safetyTimer);
        // eslint-disable-next-line no-console
        console.warn('[paywall] refreshUser failed:', err);
        setVerifyFailed(true);
        setVerified(true);
      });
    return () => { cancelled = true; window.clearTimeout(safetyTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Until the server confirms the user, show a neutral loader — NEVER the
  // dashboard. This is the architectural fix: stale AuthContext can never
  // paint the protected UI even for a single frame.
  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg-muted text-sm">
        Loading…
      </div>
    );
  }
  if (verifyFailed) {
    return <Navigate to="/signin" replace />;
  }
  if (isTrialExpired(user)) return <TrialExpired />;
  return children;
}

function PaymentSuccessWatcher() {
  usePaymentSuccessSync();
  return null;
}

function PublicOnly({ children }: { children: React.ReactElement }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}
